import React, { useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { createRoot } from 'react-dom/client';
import homePageHeadHtml from './homePageHead.html';
import homePageBodyHtml from './homePageBody.html';
import homePageTailHtml from './homePageTail.html';
import homePageRootAttributes from './homePageRootAttributes.json';
import UploadSyllabusCard from './UploadSyllabusCard';

const SOURCE_URL =
  'https://www.pearson.com/en-us/higher-education/products-services/pearsonplus/etextbooks-students.html';

/** Saved HTML uses root-relative AEM paths; they only work on pearson.com — point them at the live site. */
const PEARSON_SITE_ORIGIN = 'https://www.pearson.com';

function defaultPearsonAssetBase() {
  const pub = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  if (!pub) return '/salesbot-assets/';
  return `${pub}/salesbot-assets/`;
}

/**
 * Pearson marketing home snapshot (same behavior as hack `HomeContainer` + `App` pairing).
 * Static files live under `public/salesbot-assets/assets` so we do not clobber `public/assets` (MFE).
 *
 * @param {{ assetBase?: string }} props
 * @param {string} [props.assetBase] - URL prefix whose `assets/` subfolder mirrors the Vite `public/assets` tree
 */
export default function PearsonHome({ assetBase } = {}) {
  const resolveMarkup = useMemo(() => {
    const base = (assetBase ?? defaultPearsonAssetBase()).replace(/\/?$/, '/');
    const o = PEARSON_SITE_ORIGIN;
    return (html) => {
      let out = html.replaceAll('./assets/', `${base}assets/`);
      out = out.replaceAll('="/content/dam/', `="${o}/content/dam/`);
      out = out.replaceAll("='/content/dam/", `='${o}/content/dam/`);
      out = out.replaceAll('url(/content/dam/', `url(${o}/content/dam/`);
      out = out.replaceAll('url(&#39;/content/dam/', `url(&#39;${o}/content/dam/`);
      // srcset=" then newline + indent + /content/dam/... (saved AEM markup) — without this, <picture> sources 404 on the dev host
      out = out.replace(/\n(\s*)\/content\/dam\//g, (_, indent) => `\n${indent}${o}/content/dam/`);
      return out;
    };
  }, [assetBase]);

  const headResolved = useMemo(() => resolveMarkup(homePageHeadHtml), [resolveMarkup]);
  const bodyResolved = useMemo(() => resolveMarkup(homePageBodyHtml), [resolveMarkup]);
  const tailResolved = useMemo(() => resolveMarkup(homePageTailHtml), [resolveMarkup]);

  const headNodesRef = useRef([]);
  const bodyMountRef = useRef(null);
  const uploadRootRef = useRef(null);

  useLayoutEffect(() => {
    const node = bodyMountRef.current;
    if (!node) return undefined;

    node.innerHTML = bodyResolved;
    const slot = node.querySelector('#react-upload-syllabus-slot');
    if (slot) {
      uploadRootRef.current?.unmount();
      const root = createRoot(slot);
      uploadRootRef.current = root;
      root.render(<UploadSyllabusCard />);
    }

    return () => {
      uploadRootRef.current?.unmount();
      uploadRootRef.current = null;
      node.textContent = '';
    };
  }, [bodyResolved]);

  useLayoutEffect(() => {
    document.documentElement.lang = 'en-US';
    document.documentElement.classList.add('no-js');
    document.documentElement.style.setProperty('--he-scrollPaddingTop', '191px');

    const Parser = window.DOMParser;
    const parser = new Parser();
    const doc = parser.parseFromString(
      `<!DOCTYPE html><html><head>${headResolved}</head><body></body></html>`,
      'text/html'
    );

    const appended = [];
    Array.from(doc.head.childNodes).forEach((child) => {
      const clone = document.importNode(child, true);
      document.head.appendChild(clone);
      appended.push(clone);
    });
    headNodesRef.current = appended;

    return () => {
      headNodesRef.current.forEach((n) => {
        n.parentNode?.removeChild(n);
      });
      headNodesRef.current = [];
      document.documentElement.classList.remove('no-js');
      document.documentElement.style.removeProperty('--he-scrollPaddingTop');
    };
  }, [headResolved]);

  return (
    <>
      <noscript>
        <p>
          This view is a local snapshot of <a href={SOURCE_URL}>Pearson eTextbooks for students</a>. Enable JavaScript
          for the full saved layout.
        </p>
      </noscript>
      <div {...homePageRootAttributes} ref={bodyMountRef} suppressHydrationWarning />
      {/* eslint-disable-next-line react/no-danger -- tail is static saved marketing HTML */}
      <div dangerouslySetInnerHTML={{ __html: tailResolved }} suppressHydrationWarning />
    </>
  );
}

PearsonHome.propTypes = {
  assetBase: PropTypes.string
};

PearsonHome.defaultProps = {
  assetBase: undefined
};
